import { useSelector } from "react-redux"
import { selectLoadingState } from "../../../../redux/customer"
import { ComplianceFilterOptions } from "../types"
import { ComplianceStatus, LoadingState } from "../../../../types"
import { Checkbox, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { DangerLinearProgress, InactiveLinearProgress, SuccessLinearProgress, WarningLinearProgress } from "../../../../components/LinearProgress.styles"

type ComplianceStatusTableProps = {
  complianceCounts: Record<ComplianceStatus, number> | null,
  complianceFilter: ComplianceFilterOptions,
  compliancePercentages: Record<ComplianceStatus, number> | null,
  handleComplianceFilter: (filter: ComplianceFilterOptions) => void,
}

const ComplianceStatusTable = (props: ComplianceStatusTableProps) => {
  const {
    complianceCounts,
    complianceFilter,
    compliancePercentages,
    handleComplianceFilter,
  } = props

  const loadingState = useSelector(selectLoadingState)
  const isLoaded = loadingState === LoadingState.Done && complianceCounts && compliancePercentages

  return (
    <Table size="small" data-testid="compliance-status-table">
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '10%' }}>Practices</TableCell>
          <TableCell sx={{ width: '30%' }}></TableCell>
          <TableCell sx={{ width: '40%' }}>Status</TableCell>
          <TableCell sx={{ width: '20%' }}>Filter by</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>
            {
              isLoaded
                ? <span className="compliance-box background-danger">{complianceCounts[ComplianceStatus.NotStarted]}</span>
                : <Skeleton variant="rounded" width={24} height={23} />
            }
          </TableCell>
          <TableCell>
            {
              isLoaded
                ? <DangerLinearProgress variant="determinate" value={compliancePercentages[ComplianceStatus.NotStarted]} />
                : <Skeleton variant="rounded" height={8}></Skeleton>
            }
          </TableCell>
          <TableCell>Action Required</TableCell>
          <TableCell>
            <Checkbox
              value="not-started"
              checked={complianceFilter === 'not-started' ? true : false}
              onChange={() => handleComplianceFilter('not-started')}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            {
              isLoaded
                ? <span className="compliance-box background-warning">{complianceCounts[ComplianceStatus.InProgress]}</span>
                : <Skeleton variant="rounded" width={24} height={23} />
            }
          </TableCell>
          <TableCell>
            {
              isLoaded
                ? <WarningLinearProgress variant="determinate" value={compliancePercentages[ComplianceStatus.InProgress]} />
                : <Skeleton variant="rounded" height={8}></Skeleton>
            }
          </TableCell>
          <TableCell>In Progress</TableCell>
          <TableCell>
            <Checkbox
              value="in-progress"
              checked={complianceFilter === 'in-progress' ? true : false}
              onChange={() => handleComplianceFilter('in-progress')}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            {
              isLoaded
                ? <span className="compliance-box background-success">{complianceCounts[ComplianceStatus.Complete]}</span>
                : <Skeleton variant="rounded" width={24} height={23} />
            }
          </TableCell>
          <TableCell>
            {
              isLoaded
                ? <SuccessLinearProgress variant="determinate" value={compliancePercentages[ComplianceStatus.Complete]} />
                : <Skeleton variant="rounded" height={8}></Skeleton>
            }
          </TableCell>
          <TableCell>Completed</TableCell>
          <TableCell>
            <Checkbox
              value="complete"
              checked={complianceFilter === 'complete' ? true : false}
              onChange={() => handleComplianceFilter('complete')}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            {
              isLoaded
                ? <span className="compliance-box background-inactive">{complianceCounts[ComplianceStatus.NotApplicable]}</span>
                : <Skeleton variant="rounded" width={24} height={23} />
            }
          </TableCell>
          <TableCell>
            {
              isLoaded
                ? <InactiveLinearProgress variant="determinate" value={compliancePercentages[ComplianceStatus.NotApplicable]} />
                : <Skeleton variant="rounded" height={8}></Skeleton>
            }
          </TableCell>
          <TableCell>Service Inactive</TableCell>
          <TableCell>
            <Checkbox
              value="not-applicable"
              checked={complianceFilter === 'not-applicable' ? true : false}
              onChange={() => handleComplianceFilter('not-applicable')}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>    
  )  
}

export default ComplianceStatusTable
